// AI 建议（SOP 提议）：人话摘要 + diff；文员可采纳小改 / 不采纳 / 交给管理员；管理员可微调内容后采纳
import { useState } from "react";
import { Link } from "react-router-dom";
import type { ProposalDTO } from "@csp/shared";
import { api } from "../lib/api";
import { fmtDateTime, fmtTime } from "../lib/format";
import type { Tone } from "../lib/statusTone";
import { useAction } from "../lib/hooks";
import type { ProposalListItem } from "../lib/types";
import { Badge } from "./Badges";
import { DiffView } from "./DiffView";
import { useDialog } from "./Dialogs";

export const STATUS_TEXT: Record<ProposalDTO["status"], string> = {
  open: "待处理",
  adopted: "已采纳",
  rejected: "未采纳",
  escalated: "已交给管理员",
};

/** 提议状态色：待处理 amber（审批中）、交给管理员 orange（等我方处理）、采纳 green、未采纳 gray */
export const STATUS_TONE: Record<ProposalDTO["status"], Tone> = { open: "amber", escalated: "orange", adopted: "green", rejected: "gray" };

/** 文员采纳按钮灰掉时的原因 */
export function adoptBlockedReason(p: ProposalListItem): string | null {
  if (p.clerkCanAdopt) return null;
  if (p.status === "escalated") return "已交给管理员，由管理员决定";
  if (p.status !== "open") return "这条建议已经处理过了";
  if (p.scope === "major") return "改动较大（不只是改文字/加已知坑），需要管理员审批";
  return "该类型已成熟或近期改动较多 / 通过率在下降，暂时只能由管理员采纳";
}

export function ProposalCard({
  p,
  role,
  onChanged,
  showType = true,
}: {
  p: ProposalListItem;
  role: string;
  onChanged: () => void;
  showType?: boolean;
}) {
  const dialog = useDialog();
  const act = useAction();
  const [showDiff, setShowDiff] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const isAdmin = role === "admin";
  const closed = p.status === "adopted" || p.status === "rejected";
  const blocked = isAdmin ? (closed ? "这条建议已经处理过了" : null) : adoptBlockedReason(p);

  const adopt = async () => {
    const ok = await dialog.confirm({ title: "采纳这条建议？", body: <p>{p.summary}</p>, okText: "采纳" });
    if (!ok) return;
    const r = await act.run("adopt", () => api.adopt(p.id, editing !== null ? { content: editing } : {}));
    if (r) onChanged();
  };
  const reject = async () => {
    const note = await dialog.prompt({ title: "不采纳", label: "原因（可不填）", okText: "不采纳" });
    if (note === null) return;
    const r = await act.run("reject", () => api.reject(p.id, note || undefined));
    if (r) onChanged();
  };
  const escalate = async () => {
    const r = await act.run("escalate", () => api.escalate(p.id));
    if (r) onChanged();
  };
  const startEdit = async () => {
    const d = await act.run("load", () => api.proposal(p.id));
    if (d) setEditing(d.newContent);
  };

  return (
    <div className="card" data-testid="proposal" data-proposal-id={p.id}>
      <div className="row">
        <h2 style={{ margin: 0, flex: 1 }}>🤖 AI 建议：<span translate={p.summary ? "no" : undefined}>{p.summary || "（无摘要）"}</span></h2>
        <Badge tone={p.scope === "major" ? "orange" : "gray"}>{p.scope === "major" ? "大改" : "小改"}</Badge>
        <Badge tone={STATUS_TONE[p.status]}>{STATUS_TEXT[p.status]}</Badge>
      </div>
      <div className="small muted" style={{ margin: "6px 0 10px" }}>
        {showType && p.typeName && <>{p.typeName} · </>}
        <span title={fmtDateTime(p.createdAt)}>{fmtTime(p.createdAt)}</span>
        {p.runId && <> · 来自运行 {p.runId.slice(-6)}</>}
        {p.decidedBy && <> · 处理人 {p.decidedBy}</>}
      </div>
      {p.clerkHint && <div className="notice info">文员说：<span translate="no">{p.clerkHint}</span></div>}
      {p.reason && <p>理由：<span translate="no">{p.reason}</span></p>}
      <button className="btn-link btn small" onClick={() => setShowDiff((v) => !v)}>
        {showDiff ? "收起改动" : "看改动"}
      </button>
      {showDiff && editing === null && <DiffView diff={p.diff} maxHeight={360} />}
      {editing !== null && (
        <label className="field">
          <span className="lbl">采纳前微调（整份 SKILL.md）</span>
          <textarea className="code" value={editing} onChange={(e) => setEditing(e.target.value)} />
        </label>
      )}
      {act.error && <div className="notice err">{act.error}</div>}
      {!closed && (
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn btn-ok" disabled={!!blocked || !!act.busy} onClick={() => void adopt()} title={blocked ?? ""}>
            采纳
          </button>
          {(isAdmin || p.status === "open") && (
            <button className="btn" disabled={!!act.busy} onClick={() => void reject()}>
              不采纳
            </button>
          )}
          {p.status === "open" && !isAdmin && (
            <button className="btn" disabled={!!act.busy} onClick={() => void escalate()}>
              交给管理员
            </button>
          )}
          {isAdmin && editing === null && (
            <button className="btn" disabled={!!act.busy} onClick={() => void startEdit()}>
              微调后采纳
            </button>
          )}
          {isAdmin && p.typeId && (
            <Link className="btn-link btn" to={`/admin/types/${p.typeId}`}>
              查看类型
            </Link>
          )}
          {blocked && <span className="small muted">{blocked}</span>}
        </div>
      )}
    </div>
  );
}
